// src/ui/screens/coach.ts
// THE COACH SURFACE: the academy roster, the human identity confirmation, and session creation.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE CONFIRMATION IS A SEPARATE, DELIBERATE ACT — STRUCTURALLY, NOT AS A HABIT
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Tapping a roster row SELECTS. It does not assign. The assignment is a second call carrying a
// literal `confirmed: true`, and the backend 409s if that field is anything else — the affirmation
// is never defaulted and never inferred from the presence of a method or a QR token.
//
// This screen mirrors that shape rather than merely respecting it: the confirm button is rendered
// only after a row is selected, it names the athlete it is about to bind, and it is never the
// default action of the tap. No auto-select, no "confirm the only candidate for you", no
// pre-checked box. There is also no face matching anywhere in this app — the backend has no such
// endpoint, and its absence is the feature.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `roster_candidates_seen` IS SHOWN WHEN IT DISAGREES WITH `athlete_count`
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The backend reports both on purpose: when they differ, someone on the roster is not someone this
// caller may read. Quietly rendering the smaller list would absorb exactly the fact the backend went
// out of its way to surface.

import { el, empty, field, mount, section, spinner, why } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal } from '../../api/client';
import {
  academyToday, confirmAssignment, listRosterCandidates, resumeSessions,
  type CameraRole, type RosterCandidate, type SessionMode,
} from '../../api/practice';
import { CAMERA_ROLES, SESSION_MODES } from '../../api/practice';
import { getState, selectAthlete, setAcademy, setSession } from '../state';
import { enqueueSession, drain, listJobs } from '../../queue/queue';
import { deviceId } from '../../device/ids';

export async function coachScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  const body = el('div');

  const academyInput = el('input', {
    class: 'input mono', type: 'text', value: st.academyId ?? '',
    placeholder: 'academy id (uuid)',
  });

  mount(root,
    section('Academy',
      why('The academy id is typed, and that is safe: the server checks that you own this academy '
        + 'before it returns a single roster row. A wrong id gets a refusal, never another '
        + 'academy’s children.'),
      academyInput,
      el('button', {
        class: 'btn btn-primary',
        onclick: () => { setAcademy(academyInput.value); rerender(); },
      }, 'Load academy'),
    ),
    body,
  );

  if (!st.academyId) {
    body.append(empty('No academy loaded. Enter an academy id above to see today’s view and the roster.'));
    return;
  }

  body.append(spinner('Loading the academy…'));
  const [today, roster] = await Promise.all([
    academyToday(st.academyId),
    listRosterCandidates(st.academyId),
  ]);

  const out = el('div');

  /* ── TODAY ────────────────────────────────────────────────────────────────────────────── */
  if (isRefusal(today)) {
    out.append(section('Today', refusalView(today, { onRetry: rerender })));
  } else {
    const t = today.value;
    const rows = el('div', { class: 'list' });
    for (const a of t.athletes) {
      rows.append(el('div', { class: 'row-item' },
        el('p', { class: 'mono small' }, a.athlete_id),
        a.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
        field('Sessions today', a.sessions_today.length),
        field('Pending reviews', a.pending_reviews),
        field('Workload', a.workload_state),
        // The workload guardian's own closed set of sentences, unedited.
        ...a.workload_messages.map((m) => el('p', { class: 'note' }, m)),
        a.needs_identity_confirmation
          && el('p', { class: 'warn' },
            'A session is running with no human-confirmed identity behind it.'),
      ));
    }
    out.append(section('Today',
      field('Athletes you may read', t.athlete_count),
      field('In a net now', t.totals.in_session_now),
      field('Pending reviews', t.totals.pending_reviews),
      field('Workload flagged', t.totals.workload_flagged),
      field('Unconfirmed identity', t.totals.unconfirmed_identity),
      // Surfaced, not absorbed. See the header block.
      t.roster_candidates_seen !== t.athlete_count && el('p', { class: 'warn' },
        `${t.roster_candidates_seen} athletes are on this roster but only ${t.athlete_count} are `
        + 'ones you may read. The difference is shown rather than hidden.'),
      el('p', { class: 'note' }, t.note),
      t.athletes.length ? rows : empty('No athlete rows in today’s view.'),
    ));
  }

  /* ── ROSTER + CONFIRMATION ────────────────────────────────────────────────────────────── */
  if (isRefusal(roster)) {
    out.append(section('Roster', refusalView(roster, { onRetry: rerender })));
  } else {
    const candidates = roster.value.candidates ?? [];
    const list = el('div', { class: 'list' });
    for (const c of candidates) list.append(candidateRow(c, roster.value.academy_id, rerender));
    out.append(section('Roster — tap to select',
      el('p', { class: 'note' },
        'Tapping selects. It does not assign anybody to anything. Assignment is the separate '
        + 'confirm step below, and the server refuses it unless a human affirmed it.'),
      roster.value.hint !== undefined && roster.value.hint !== null
        && el('p', { class: 'note' }, JSON.stringify(roster.value.hint)),
      candidates.length ? list : empty('This academy’s roster came back empty.'),
    ));
  }

  out.append(await selectionPanel(rerender));
  mount(body, out);
}

function candidateRow(c: RosterCandidate, academyId: string, rerender: () => void): HTMLElement {
  const st = getState();
  const selected = st.athlete?.athleteId === c.athlete_id;
  return el('button', {
    class: `row-item row-tap${selected ? ' row-selected' : ''}`,
    onclick: () => {
      selectAthlete({
        athleteId: c.athlete_id,
        from: 'roster_candidates', // ← provenance: this id came out of the roster response
        academyId,
        label: c.display_name ?? c.athlete_id,
        subjectIsMinor: c.subject_is_minor ?? null,
      });
      rerender();
    },
  },
    el('p', { class: 'mono small' }, c.display_name ?? c.athlete_id),
    c.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
    // Rendered from the server's own flag, not assumed true because we know it always is.
    c.requires_confirmation && el('span', { class: 'tag' }, 'needs human confirmation'),
    selected && el('span', { class: 'tag tag-ok' }, 'selected'),
  );
}

/** The selected athlete: confirm identity, start a session, resume one. */
async function selectionPanel(rerender: () => void): Promise<HTMLElement> {
  const st = getState();
  if (!st.athlete) {
    return section('Selected athlete', empty('Nobody selected. Tap a roster row above.'));
  }
  const a = st.athlete;
  const wrap = el('div');
  const result = el('div');

  // A select over the real vocabulary, not free text. The default is LIVE_BOWLER because it is
  // the commonest net and — more importantly — the LOWEST-confidence input: the ball is an
  // estimate rather than a recorded machine preset. Defaulting to the weaker claim means a
  // coach who does not touch this field never over-states what the capture knows.
  const modeInput = el('select', { class: 'input' },
    ...SESSION_MODES.map((m) => el('option', { value: m }, m)));
  const roleSelect = el('select', { class: 'input' },
    ...CAMERA_ROLES.map((r) => el('option', { value: r }, r)));
  const sessionInput = el('input', {
    class: 'input mono', type: 'text', value: st.sessionId ?? '',
    placeholder: 'session id (from a created or resumed session)',
  });

  wrap.append(section(`Selected: ${a.label}`,
    el('p', { class: 'mono small' }, a.athleteId),
    a.subjectIsMinor && el('p', { class: 'warn' }, 'This athlete is a minor.'),
    el('p', { class: 'note' }, `Id provenance: ${a.from} — it came from a server response, not from input.`),

    el('h3', {}, 'Start a practice session'),
    el('p', { class: 'note' },
      'The session is QUEUED first, with its idempotency keys minted on this device before anything '
      + 'is sent. It is therefore creatable with no signal at all, and the retry is the same '
      + 'request, not a second one.'),
    el('label', { class: 'label' }, 'Mode'), modeInput,
    el('label', { class: 'label' }, 'Camera role'), roleSelect,
    el('button', {
      class: 'btn btn-primary',
      onclick: () => {
        void (async () => {
          // ── SCOPE THE SESSION TO THE ACADEMY (26 Aug 2026) ────────────────────────────────
          // This omitted academy_id, so every session was created with a null one. The backend has
          // always accepted and stored the field (session-service.ts:451); nothing here ever sent
          // it. The consequence surfaced two screens later as a 409 on identity confirmation:
          // "this practice session is not scoped to an academy, so an academy coach cannot confirm
          // identity on it". That refusal is correct — the SESSION was the thing at fault, and it
          // had been created that way minutes earlier without a word.
          //
          // `a.academyId` is the academy this athlete was ENUMERATED under, not one typed here.
          // Choosing a different academy drops the selected athlete (state.ts:64), so the two
          // cannot drift apart, which is what makes it safe to send without re-checking.
          const job = await enqueueSession(a.athleteId, {
            mode: modeInput.value as SessionMode,
            camera_role: roleSelect.value as CameraRole,
            started_at: new Date().toISOString(),
            ...(a.academyId ? { academy_id: a.academyId } : {}),
          });
          mount(result, el('p', { class: 'note' },
            `Queued as ${job.id} with client_session_uuid ${job.client_session_uuid}. `
            + 'That key will be identical on every retry.'));
          await drain();

          // ── READ THE SERVER'S ANSWER BACK AND BIND IT ──────────────────────────────
          // The queue has recorded `server_session_id` on the job since the day it was written
          // (queue.ts:243). Nothing read it. So the flow was: queue the session, send it, receive
          // the id, store the id — and then ask the user to paste an id they had never been shown.
          // There was nothing on screen to copy, and the field stayed empty however many times
          // they pressed the button.
          //
          // A failed job was invisible in the same way: `drain()` marks it failed, `rerender()`
          // redraws the same empty field, and the only trace was a counter in the header. Both
          // outcomes looked identical to the person standing at the net.
          const mine = (await listJobs()).find((j) => j.id === job.id);
          const issued = mine && mine.kind === 'session' ? mine.server_session_id : null;

          // ── rerender() DESTROYS `result`, SO IT MUST NOT RUN AFTER A MESSAGE ──────────────
          // My first version mounted one of the three messages below and then called rerender()
          // unconditionally on the next line. rerender() redraws this whole screen from state, so
          // the freshly-mounted node was discarded in the same tick — the message existed for
          // microseconds and the field stayed blank. Exactly the defect this block was written to
          // fix, reintroduced one line under the fix.
          //
          // Success rerenders BECAUSE the state changed and the field repopulates from it.
          // Failure must NOT: there is nothing new in state to draw, and redrawing is what threw
          // the reason away.
          if (issued) {
            setSession(issued);
            sessionInput.value = issued;
            rerender();                        // state changed — redraw, the field fills from it
            return;
          }

          if (mine && mine.state === 'failed') {
            // Say what actually happened. The queue keeps the refusal on the job; a job that
            // failed for a reason the person could act on is worth more than a red number.
            mount(result, el('p', { class: 'warn' },
              `The session was not created: ${mine.last_error ?? 'the server refused it'}`
              + (mine.last_error_kind ? ` [${mine.last_error_kind}]` : '')
              + '. It stays in the queue — the Queue tab shows it, and a retry sends the same '
              + 'request rather than a second one.'));
            return;                            // do NOT rerender: it would erase the reason
          }

          mount(result, el('p', { class: 'note' },
            'Still queued — nothing has been sent yet. This is the normal state with no signal; '
            + 'it will send when the connection returns, and the id appears here then.'));
        })();
      },
    }, 'Queue a session'),
    result,

    el('h3', {}, 'Bind a session id'),
    el('p', { class: 'note' },
      'A session id is only ever a value the server issued — from creating one, or from Resume '
      + 'below. Uploads and identity confirmation both need it.'),
    sessionInput,
    el('button', {
      class: 'btn',
      onclick: () => { setSession(sessionInput.value.trim() || null); rerender(); },
    }, 'Use this session'),

    el('h3', {}, 'Confirm identity — a human affirms'),
    el('p', { class: 'note' },
      'This is the act the backend requires. It sends confirmed: true, and the server still checks '
      + 'that you own this academy and that this athlete holds an active enrollment in it. There is '
      + 'no face matching anywhere in this product.'),
    confirmBlock(a.athleteId, a.academyId, a.label),
  ));

  wrap.append(await resumePanel(a.athleteId, rerender));
  return wrap;
}

function confirmBlock(athleteId: string, academyId: string | null, label: string): HTMLElement {
  const out = el('div');
  const st = getState();
  if (!academyId) return empty('No academy context for this athlete, so no assignment can be confirmed.');
  if (!st.sessionId) return empty('Bind a session id above before confirming an identity for it.');

  const btn = el('button', {
    class: 'btn btn-primary',
    onclick: () => {
      void (async () => {
        mount(out, spinner('Confirming…'));
        const r = await confirmAssignment(academyId, {
          session_id: st.sessionId as string,
          athlete_id: athleteId,
          method: 'ROSTER_TAP',
          confirmed: true, // literal true — the type forbids anything else
        });
        if (isRefusal(r)) { mount(out, refusalView(r)); return; }
        mount(out,
          el('p', { class: 'ok' }, `Confirmed by ${r.value.confirmed_by} at ${r.value.confirmed_at}.`),
          field('Method', r.value.method),
          r.value.subject_is_minor && el('p', { class: 'warn' }, 'The subject is a minor.'));
      })();
    },
  }, `Confirm this is ${label}`);

  return el('div', {}, btn, out);
}

async function resumePanel(athleteId: string, rerender: () => void): Promise<HTMLElement> {
  const device = await deviceId();
  const r = await resumeSessions(athleteId, device);
  if (isRefusal(r)) return section('Resume', refusalView(r, { onRetry: rerender }));
  const rows = r.value.resumable ?? [];
  return section('Resume — sessions this device left open',
    el('p', { class: 'note' },
      'Everything not both completed and synced. Rejoining one is how the app avoids starting a '
      + 'second session for the same net.'),
    rows.length
      ? el('div', { class: 'list' }, ...rows.map((s) => el('button', {
          class: 'row-item row-tap',
          onclick: () => { setSession(s.id); rerender(); },
        },
          el('p', { class: 'mono small' }, s.id),
          field('Mode', s.mode), field('Camera', s.camera_role),
          field('Started', s.started_at),
          // The backend writes a sentence for the athlete; show it rather than a status word.
          typeof s.retry_state === 'string' && el('p', { class: 'note' }, s.retry_state),
        )))
      : empty('This device has no open sessions on the server.'),
  );
}
